import React, { useState } from 'react';
import styled from 'styled-components';
import { KeyRound, User, FileText, AlertCircle, ArrowLeft, CheckCircle, Lock, Eye, EyeOff, Check, X } from 'lucide-react';
import { API_BASE } from '../../services/api';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #008752 0%, #006B41 50%, #004D30 100%);
  padding: ${({ theme }) => theme.spacing.lg};
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.lg};
  left: ${({ theme }) => theme.spacing.lg};
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateX(-2px);
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  width: 100%;
  max-width: 450px;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const LogoIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #008752 0%, #006B41 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: center;
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const InputGroup = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textSecondary};
  z-index: 1;
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.md} 48px;
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${({ theme }) => theme.spacing.md};
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const PasswordRequirements = styled.div`
  background: ${({ theme }) => theme.colors.gray50};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const RequirementTitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const RequirementsList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const RequirementItem = styled.div<{ $met: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ $met, theme }) => 
    $met ? theme.colors.success : theme.colors.textSecondary};
  transition: color 0.2s;
`;

const RequirementIcon = styled.div<{ $met: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $met, theme }) => 
    $met ? theme.colors.success : theme.colors.gray200};
  color: white;
  flex-shrink: 0;
  transition: all 0.2s;
`;

const Button = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  background: linear-gradient(135deg, #008752 0%, #006B41 100%);
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: ${({ theme }) => theme.colors.error}10;
  border: 1px solid ${({ theme }) => theme.colors.error}30;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const SuccessMessage = styled.div`
  background: ${({ theme }) => theme.colors.success}10;
  border: 1px solid ${({ theme }) => theme.colors.success}30;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.success};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const InfoBox = styled.div`
  background: ${({ theme }) => theme.colors.primary}10;
  border: 1px solid ${({ theme }) => theme.colors.primary}30;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const InfoText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: 0;
  line-height: 1.5;
`;

interface ForgotPasswordProps {
  onBack: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack }) => {
  const [username, setUsername] = useState('');
  const [nit, setNit] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Validación de requisitos de contraseña
  const checkPasswordRequirements = (password: string) => {
    return {
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
  };

  const passwordRequirements = checkPasswordRequirements(newPassword);
  const allRequirementsMet = Object.values(passwordRequirements).every(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username.trim() || !nit.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      setError('Por favor, complete todos los campos');
      return;
    }

    // Validar requisitos de contraseña
    if (!allRequirementsMet) {
      setError('La contraseña no cumple con todos los requisitos de seguridad');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username.trim(),
          nit: nit.trim(),
          new_password: newPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al resetear contraseña');
      }

      setSuccess(true);
      setError('');
      
      // Redirigir al login después de 3 segundos
      setTimeout(() => {
        onBack();
      }, 3000);

    } catch (error: any) {
      setError(error.message || 'Error al resetear contraseña');
      setSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Container>
        <Card>
          <Logo>
            <LogoIcon>
              <CheckCircle size={40} color="white" />
            </LogoIcon>
            <Title>¡Contraseña Actualizada!</Title>
            <Subtitle>Tu contraseña ha sido cambiada exitosamente</Subtitle>
          </Logo>

          <SuccessMessage>
            <CheckCircle size={20} />
            Redirigiendo al inicio de sesión...
          </SuccessMessage>

          <Button type="button" onClick={onBack} style={{ marginTop: '16px' }}>
            Ir al Login Ahora
          </Button>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <BackButton onClick={onBack}>
        <ArrowLeft size={20} />
        Volver al Login
      </BackButton>
      
      <Card>
        <Logo>
          <LogoIcon>
            <KeyRound size={40} color="white" />
          </LogoIcon>
          <Title>Recuperar Contraseña</Title>
          <Subtitle>Ingresa tus datos para crear una nueva contraseña</Subtitle>
        </Logo>

        <InfoBox>
          <InfoText>
            💡 Para verificar tu identidad, necesitamos tu nombre de usuario y NIT registrado.
          </InfoText>
        </InfoBox>

        <Form onSubmit={handleSubmit}>
          {error && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {error}
            </ErrorMessage>
          )}

          <InputGroup>
            <InputIcon>
              <User size={20} />
            </InputIcon>
            <Input
              type="text"
              placeholder="Nombre de Usuario"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading}
            />
          </InputGroup>

          <InputGroup>
            <InputIcon>
              <FileText size={20} />
            </InputIcon>
            <Input
              type="text"
              placeholder="NIT (Ej: 900123456-1)"
              value={nit}
              onChange={(e) => setNit(e.target.value)}
              disabled={isLoading}
            />
          </InputGroup>

          <InputGroup>
            <InputIcon>
              <Lock size={20} />
            </InputIcon>
            <PasswordInputWrapper>
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Nueva Contraseña"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                style={{ paddingRight: '48px' }}
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </PasswordToggle>
            </PasswordInputWrapper>
          </InputGroup>

          {newPassword && (
            <PasswordRequirements>
              <RequirementTitle>Requisitos de contraseña:</RequirementTitle>
              <RequirementsList>
                <RequirementItem $met={passwordRequirements.length}>
                  <RequirementIcon $met={passwordRequirements.length}>
                    {passwordRequirements.length ? <Check size={10} /> : <X size={10} />}
                  </RequirementIcon>
                  Mínimo 12 caracteres
                </RequirementItem>
                <RequirementItem $met={passwordRequirements.uppercase}>
                  <RequirementIcon $met={passwordRequirements.uppercase}>
                    {passwordRequirements.uppercase ? <Check size={10} /> : <X size={10} />}
                  </RequirementIcon>
                  Una mayúscula
                </RequirementItem>
                <RequirementItem $met={passwordRequirements.lowercase}>
                  <RequirementIcon $met={passwordRequirements.lowercase}>
                    {passwordRequirements.lowercase ? <Check size={10} /> : <X size={10} />}
                  </RequirementIcon>
                  Una minúscula
                </RequirementItem>
                <RequirementItem $met={passwordRequirements.number}>
                  <RequirementIcon $met={passwordRequirements.number}>
                    {passwordRequirements.number ? <Check size={10} /> : <X size={10} />}
                  </RequirementIcon>
                  Un número
                </RequirementItem>
                <RequirementItem $met={passwordRequirements.special}>
                  <RequirementIcon $met={passwordRequirements.special}>
                    {passwordRequirements.special ? <Check size={10} /> : <X size={10} />}
                  </RequirementIcon>
                  Carácter especial
                </RequirementItem>
              </RequirementsList>
            </PasswordRequirements>
          )}

          <InputGroup>
            <InputIcon>
              <Lock size={20} />
            </InputIcon>
            <PasswordInputWrapper>
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirmar Contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                style={{ paddingRight: '48px' }}
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </PasswordToggle>
            </PasswordInputWrapper>
          </InputGroup>

          <Button type="submit" disabled={isLoading || !allRequirementsMet}>
            {isLoading ? 'Actualizando...' : 'Actualizar Contraseña'}
            {!isLoading && <KeyRound size={20} />}
          </Button>
        </Form>
      </Card>
    </Container>
  );
};

export default ForgotPassword;

